import { useEffect, useState } from 'react';
import { toJS } from 'mobx';
import { useStores } from '../../stores';
import {
  buildFilterFlowData,
  recalculateEachCohort,
} from '../helper-utils/cohortModalHelper';

export interface UseCohortOnPageLoadProps {
  fetchTotalResults: () => Promise<{
    noOfMemberIds: number;
    member_unique_ids: string[];
  }>;
  enabled: boolean;
  isInternal?: boolean;
  type?: string;
  deps?: unknown[];
}

export const useCohortOnPageLoad = ({
  fetchTotalResults,
  enabled,
  isInternal = false,
  type,
  deps = [],
}: UseCohortOnPageLoadProps) => {
  const {
    cohortDetailsStore,
    quaterlyReportStore,
    employerGroupStore,
  } = useStores();

  const eg_nid = employerGroupStore.employerGroupResponseData.eg_nid;
  const {
    incurredStartDate,
    incurredEndDate,
    paidStartDate,
    paidEndDate,
    aggregationParameter,
  } = quaterlyReportStore;

  const [totalResults, setTotalResults] = useState<number>(0);
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const [isInitializing, setIsInitializing] = useState(true);

  const startDate =
    aggregationParameter === 'paid' ? paidStartDate : incurredStartDate;
  const endDate =
    aggregationParameter === 'paid' ? paidEndDate : incurredEndDate;
  const max_claim_date =
    employerGroupStore.employerGroupResponseData.max_claim_date;

  useEffect(() => {
    if (!enabled) {
      return;
    }
    setIsInitializing(true);
    cohortDetailsStore.resetMemberData();

    const init = async () => {
      try {
        const { noOfMemberIds, member_unique_ids } = await fetchTotalResults();
        setTotalResults(noOfMemberIds);
        setMemberIds(member_unique_ids);

        const cohortDetailsList = toJS(cohortDetailsStore.cohortDetailsList);
        const cohortSelectionsTypes = toJS(
          cohortDetailsStore.cohortSelectionsTypes,
        );
        const hasConditions = cohortDetailsStore.conditionsDetails.length > 0;

        if (hasConditions) {
          const {
            updatedCohortList,
            finalMemberIds,
          } = await recalculateEachCohort(
            cohortDetailsList,
            noOfMemberIds,
            startDate,
            endDate,
            eg_nid,
            type,
            isInternal ? member_unique_ids : [],
            max_claim_date,
          );

          cohortDetailsStore.addMemberUniqueIdList(finalMemberIds);
          cohortDetailsStore.addClaimIdList([]);

          cohortDetailsStore.setCohortDetailsList(updatedCohortList);

          const updatedFlowData = buildFilterFlowData(
            updatedCohortList,
            cohortSelectionsTypes,
            noOfMemberIds,
          );
          cohortDetailsStore.setDraftFilterFlowData(updatedFlowData);
        }
      } catch (error) {
        console.error('Failed to initialize cohort on page load:', error);
      } finally {
        setIsInitializing(false);
      }
    };

    init();
  }, [
    enabled,
    incurredStartDate,
    incurredEndDate,
    paidStartDate,
    paidEndDate,
    aggregationParameter,
    ...deps,
  ]);

  return {
    totalResults,
    memberIds,
    isInitializing,
  };
};
